import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, Settings, LayoutDashboard, Repeat2, Waves, Heart, MessagesSquare } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/marketplace/NotificationBell";
import api from "@/lib/api";
import { ServiceMenu, MobileServiceMenu, RequestCta } from "@/components/layout/RequestMenu";
import { LanguageSwitch } from "@/components/layout/LanguageSwitch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Fallback used if /api/navigation/main is empty or unreachable. Also used
// as a safety net when the API returns a suspiciously tiny menu (< 3 items)
// so a mis-configured or partially-clobbered nav row doesn't leave the site
// header showing only "Home".
const FALLBACK_NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/book-local", label: "Book Local" },
  { to: "/hire-digital", label: "Hire Digital" },
  { to: "/request-a-service", label: "Request a Service" },
  { to: "/categories", label: "Browse Categories" },
  { to: "/deals", label: "Deals" },
  { to: "/become-a-provider", label: "Become a Provider" },
  { to: "/help", label: "Help" },
];

const REQUEST_FALLBACK = [
  { to: "/book-local", label: "Local", display: "local" },
  { to: "/hire-digital", label: "Digital", display: "digital" },
  { to: "/how-it-works/customers", label: "How It Works" },
  { to: "/deals", label: "Deals" },
  { to: "/request-a-service", label: "Get Estimates", display: "button" },
];

function useNavMenu(key, fallback) {
  const [items, setItems] = useState(fallback);
  useEffect(() => {
    let alive = true;
    api.get(`/navigation/${key}`)
      .then((r) => {
        const arr = (r.data?.items || []).filter(Boolean);
        // Defensive: any nav that trims down to < 3 items is treated as
        // broken / partially seeded — the fallback protects the shell from
        // rendering a one-item stub header.
        if (alive && arr.length >= 3) {
          setItems(arr.map((it) => ({
            to: it.href || "/",
            label: it.label,
            role: it.role,
            display: it.display,
            children: (it.children || []).map((c) => ({
              to: c.href || "/",
              label: c.label,
              target: c.target,
              role: c.role,
            })),
            target: it.target,
            end: it.end,
          })));
        }
      })
      .catch(() => { /* silent — keep fallback */ });
    return () => { alive = false; };
  }, [key]);
  return items;
}

export default function Header({ menuKey = "main" }) {
  const { user, logout, setActiveMode } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuItems = useNavMenu(menuKey, menuKey === "main" ? FALLBACK_NAV : REQUEST_FALLBACK);
  // Filter by role visibility if the item specifies one
  const activeRole = user?.active_mode || (user ? "customer" : "visitor");
  const visible = menuItems.filter((it) => {
    if (!it.role) return true;
    if (it.role === "visitor") return !user;
    if (it.role === "authenticated") return !!user;
    return activeRole === it.role || (user?.roles || []).includes(it.role);
  });
  const cta = visible.find((it) => it.display === "button");
  const NAV = visible.filter((it) => it.display !== "button");
  const isService = (n) => n.display === "local" || n.display === "digital";

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  const initials = (user?.display_name || user?.email || "GC")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const isProviderMode = user?.active_mode === "provider";
  const canSwitch = (user?.available_modes || []).length > 1;

  const onSwitchMode = async () => {
    const next = isProviderMode ? "customer" : "provider";
    try {
      await setActiveMode(next);
    } catch (_) {
      /* ignored */
    }
  };

  return (
    <header
      data-testid={menuKey === "main" ? "site-header" : `site-header-${menuKey}`}
      className={`fixed inset-x-0 top-0 z-50 transition-shadow duration-200 bg-white border-b border-slate-200 ${
        scrolled ? "shadow-[0_2px_16px_rgba(7,27,34,0.06)]" : ""
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-4 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#071b22] text-white overflow-hidden">
            <Waves className="h-5 w-5 text-[#37768e] group-hover:animate-slow-drift" />
          </span>
          <span className="font-display font-semibold tracking-tight text-[#071b22] text-lg leading-none whitespace-nowrap">
            Gulf Coast <span className="text-[#165a71]">Work</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4" aria-label="Primary">
          {NAV.map((n) => (
            isService(n) ? <ServiceMenu key={n.label} item={n} />
            : (n.children && n.children.length > 0)
              ? <HeaderDropdown key={n.to || n.label} item={n} />
              : (
                <NavLink
                  key={n.to || n.label}
                  to={n.to}
                  end={n.end}
                  target={n.target || undefined}
                  rel={n.target === "_blank" ? "noreferrer" : undefined}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3.5 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                      isActive
                        ? "text-[#071b22] bg-[#165a71]/10"
                        : "text-[#252A34]/80 hover:text-[#071b22] hover:bg-[#f1f5f9]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              )
          ))}
        </nav>

        <div className="flex-1" />

        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitch className="mr-1" />
          {cta && <RequestCta item={cta} className="mr-1" />}
          {user ? (
            <>
              {canSwitch && (
                <button
                  onClick={onSwitchMode}
                  data-testid="header-mode-switch"
                  className="inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full border border-[#165a71]/30 bg-white px-3.5 py-1.5 text-sm font-medium text-[#165a71] hover:bg-[#165a71]/5 transition-colors"
                >
                  <Repeat2 className="h-4 w-4" />
                  <span>{isProviderMode ? "Provider mode" : "Customer mode"}</span>
                </button>
              )}
              <Link to="/favorites" data-testid="header-favorites" className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-[#252A34]" aria-label="Favorites">
                <Heart className="h-4 w-4" />
              </Link>
              <Link to="/dashboard/messages" data-testid="header-messages" className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-[#252A34]" aria-label="Messages">
                <MessagesSquare className="h-4 w-4" />
              </Link>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1.5 hover:border-[#165a71]/40 transition-colors"
                    data-testid="header-account-menu"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={user.picture || undefined} alt={user.display_name} />
                      <AvatarFallback className="bg-[#165a71] text-white text-xs">{initials}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-[#071b22] max-w-[120px] truncate">{user.display_name}</span>
                    <ChevronDown className="h-3.5 w-3.5 text-[#252A34]/60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl">
                  <DropdownMenuLabel className="font-display">
                    <div className="text-sm text-[#071b22]">{user.display_name}</div>
                    <div className="text-xs font-normal text-[#252A34]/60 truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/account/settings")} data-testid="menu-settings">
                    <Settings className="h-4 w-4" /> Account settings
                  </DropdownMenuItem>
                  {user.roles?.includes("provider") && (
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4" /> Provider dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                    <LogOut className="h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-signin"
                className="whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-[#071b22] hover:bg-slate-100 transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="header-signup"
                className="whitespace-nowrap rounded-full bg-[#165a71] px-4 py-2 text-sm font-medium text-white hover:bg-[#11485a] transition-colors shadow-sm"
              >
                Create account
              </Link>
            </>
          )}
        </div>

        <LanguageSwitch className="lg:hidden mr-2" testId="lang-switch-mobile" />
        <button
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white/80"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Open menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-xl">
          <div className="px-6 py-4 space-y-1">
            {cta && <RequestCta item={cta} className="w-full mb-2 py-2.5" testId="mrnav-cta" onDone={() => setMobileOpen(false)} />}
            {NAV.map((n) => (
              isService(n) ? <MobileServiceMenu key={n.label} item={n} onClose={() => setMobileOpen(false)} />
              : (n.children && n.children.length > 0)
                ? <MobileAccordion key={n.to || n.label} item={n} onClose={() => setMobileOpen(false)} />
                : (
                  <NavLink
                    key={n.to || n.label}
                    to={n.to}
                    end={n.end}
                    target={n.target || undefined}
                    rel={n.target === "_blank" ? "noreferrer" : undefined}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-3.5 py-2.5 text-sm font-medium ${
                        isActive ? "text-[#071b22] bg-[#165a71]/10" : "text-[#252A34]/80"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                )
            ))}
            <div className="border-t border-slate-200/60 my-2" />
            {user ? (
              <>
                {canSwitch && (
                  <button
                    className="w-full text-left rounded-xl px-3.5 py-2.5 text-sm font-medium text-[#165a71]"
                    onClick={onSwitchMode}
                    data-testid="mobile-mode-switch"
                  >
                    Switch to {isProviderMode ? "customer" : "provider"} mode
                  </button>
                )}
                <NavLink to="/account/settings" onClick={() => setMobileOpen(false)} className="block rounded-xl px-3.5 py-2.5 text-sm text-[#252A34]" data-testid="mobile-settings">
                  Account settings
                </NavLink>
                <button onClick={async () => { await logout(); setMobileOpen(false); navigate("/"); }} className="block w-full text-left rounded-xl px-3.5 py-2.5 text-sm text-red-600" data-testid="mobile-logout">
                  Sign out
                </button>
              </>
            ) : (
              <div className="flex gap-2 pt-2">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-center text-sm font-medium">Sign in</Link>
                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 rounded-full bg-[#165a71] text-white px-4 py-2.5 text-center text-sm font-medium">Create account</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

// ---------- Desktop dropdown -------------------------------------------
function HeaderDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const testKey = item.label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="relative" ref={wrapRef} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); } }}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`nav-${testKey}`}
        className={`inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-full transition-colors ${open ? "text-[#071b22] bg-[#165a71]/10" : "text-[#252A34]/80 hover:text-[#071b22] hover:bg-[#f1f5f9]"}`}
      >
        {item.label}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`nav-dropdown-${testKey}`}
          className="absolute left-0 top-full mt-1 min-w-[220px] rounded-2xl border border-slate-200 bg-white shadow-lg py-2 z-50"
        >
          {(item.children || []).map((c) => (
            <NavLink
              key={c.to || c.label}
              to={c.to}
              target={c.target || undefined}
              rel={c.target === "_blank" ? "noreferrer" : undefined}
              role="menuitem"
              data-testid={`nav-dropdown-item-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `block px-4 py-2 text-sm ${isActive ? "text-[#071b22] bg-[#165a71]/5" : "text-[#252A34]/80 hover:bg-slate-50 hover:text-[#071b22]"}`}
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Mobile accordion -------------------------------------------
function MobileAccordion({ item, onClose }) {
  const [open, setOpen] = useState(false);
  const testKey = item.label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`mnav-${testKey}`}
        className="w-full flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-medium text-[#252A34]/80"
      >
        <span>{item.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-3 mt-1 space-y-1" data-testid={`mnav-children-${testKey}`}>
          {(item.children || []).map((c) => (
            <NavLink
              key={c.to || c.label}
              to={c.to}
              target={c.target || undefined}
              rel={c.target === "_blank" ? "noreferrer" : undefined}
              onClick={onClose}
              data-testid={`mnav-child-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `block rounded-lg px-3.5 py-2 text-sm ${isActive ? "text-[#071b22] bg-[#165a71]/10" : "text-[#252A34]/80"}`}
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
